import styled, {
  breakpoints,
  css,
  Device,
  Icon,
  IconName,
  IFalconTheme,
  Layout,
  ResponsiveTypographyVariant,
  StyledComponent,
  Tooltip,
  TooltipPosition,
  withTheme,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';

import { TranslationsContext } from '../../common/contexts';
import { FROM_LABEL } from '../../common/types/constants';
import CostView from '../CostView';
import { PriceValueVariant } from './PriceValue.interfaces';
import { CustomLayoutWrapper, CustomTypography, LeadRateContainer } from './PriceValue.styles';

export interface IPriceValueTranslationData {
  leadRateLabel: string;
  leadRateUnitLabel: string;
  leadRateTooltip: string;
}

export interface IPriceValueProps {
  className?: string;
  leadRate: string;
  hideRackRate?: boolean;
  rackRate: string;
  showTooltip: boolean;
  theme?: IFalconTheme;
  tooltipPosition?: TooltipPosition;
  translationData: IPriceValueTranslationData;
  variant?: PriceValueVariant;
}

/**
 * The price info tooltip should overlap the sales labels
 */
const PRICE_INFO_DESKTOP_ZINDEX: number = 4;
const PRICE_INFO_MOBILE_ZINDEX: number = 2;

const CustomTooltip: StyledComponent<typeof Tooltip, IFalconTheme, {}> = styled(Tooltip)`
  z-index: ${PRICE_INFO_MOBILE_ZINDEX};

  @media (min-width: ${breakpoints[Device.tablet]}) {
    z-index: ${PRICE_INFO_DESKTOP_ZINDEX};
  }
`;

const Wrapper: StyledComponent<typeof Layout.Container, IFalconTheme, {}> = styled(
  Layout.Container
)(
  ({ theme }: { theme?: IFalconTheme }) => css`
    font-family: ${theme.fontFamilyBody.join(',')};
  `
);

const PriceValue: React.FunctionComponent<IPriceValueProps> = ({
  className,
  leadRate,
  hideRackRate,
  rackRate,
  showTooltip,
  theme,
  tooltipPosition,
  translationData,
  variant,
}) => {
  return (
    <TranslationsContext.Consumer>
      {({ translations }) => {
        const { leadRateLabel, leadRateUnitLabel, leadRateTooltip } = translationData;
        const translatedPrefix: string = i18n(FROM_LABEL, [], translations);
        let tooltip: JSX.Element;
        if (showTooltip) {
          tooltip = (
            <CustomLayoutWrapper>
              <CustomTooltip
                content={leadRateTooltip}
                position={tooltipPosition || TooltipPosition.top}
              >
                <Icon name={IconName.info} fillColor={theme.color.blackText} />
              </CustomTooltip>
            </CustomLayoutWrapper>
          );
        }

        const typographyVariant: ResponsiveTypographyVariant =
          variant === PriceValueVariant.small
            ? ResponsiveTypographyVariant.nano
            : ResponsiveTypographyVariant.micro;

        const textColor: string =
          variant === PriceValueVariant.small ? theme.color.blackText : theme.color.black;

        return (
          <Wrapper
            flexDirection="column"
            alignItems="flex-start"
            className={`priceValueContainer ${className}`}
          >
            <CostView
              translatedPrefix={translatedPrefix}
              translatedSuffix={leadRateUnitLabel}
              theme={theme}
              leadRate={leadRate}
              rackRate={rackRate}
              hideRackRate={hideRackRate}
              styles={{ priceColor: theme.color.primary, textColor }}
              variant={variant}
            />
            <LeadRateContainer className="leadRateContainer">
              <CustomTypography variant={typographyVariant} color={textColor}>
                {leadRateLabel}
              </CustomTypography>
              {tooltip}
            </LeadRateContainer>
          </Wrapper>
        );
      }}
    </TranslationsContext.Consumer>
  );
};

export default withTheme(PriceValue);
